import React, { useEffect, useState } from 'react';
import { Grid, FormGroup, FormControlLabel } from '@mui/material';
import MUICheckbox from '../MUICheckbox/MUICheckbox';

interface IMenuData {
    menuId: number;
    roleId: number;
    sortOrder: number;
    isDisplay: boolean;
    menuName: string;
    menuFeaturePermissions: MenuFeaturePermission[];
}

interface MenuFeaturePermission {
    menuFeatureId: number;
    menuFeature: string;
    hasPermission: boolean;
}

interface SelectedMenuId {
    menuFeatureId: number;
}

interface PermissionTabContentProps {
    menuData: IMenuData | null;
    index: number;
    selected: boolean;
    onItemSelect: (menuObj: SelectedMenuId, checked: boolean, menuType: string) => void;
    selectedMenuFeatureIds: (number | SelectedMenuId)[];
}

function PermissionTabContent({
    menuData,
    onItemSelect,
    selectedMenuFeatureIds,
}: PermissionTabContentProps): JSX.Element {
    const [permissions, setPermissions] = useState<MenuFeaturePermission[]>([]);
    const [mainMenuSelectStatus, setMainMenuSelectedStatus] = useState<'all' | 'partial' | ''>('');

    // Handle menuData change and set permissions initially
    useEffect(() => {
        if (menuData) {
            setPermissions(menuData.menuFeaturePermissions);
            updateMainMenuStatus(menuData.menuFeaturePermissions);
        }
    }, [menuData]);

    // Update permission state based on external selections
    useEffect(() => {
        if (menuData && selectedMenuFeatureIds.length > 0) {
            const menuFeatureIds = selectedMenuFeatureIds.map(item =>
                typeof item === 'object' && item !== null ? item.menuFeatureId : item
            );

            const newPermissions = menuData.menuFeaturePermissions.map(permission => ({
                ...permission,
                hasPermission: menuFeatureIds.includes(permission.menuFeatureId),
            }));

            setPermissions(newPermissions);
            updateMainMenuStatus(newPermissions);
        }
    }, [selectedMenuFeatureIds, menuData]);

    // Function to update main menu checkbox status (all/partial/none)
    const updateMainMenuStatus = (updatedPermissions: MenuFeaturePermission[]): void => {
        const totalPermissions = updatedPermissions.length;
        const selectedPermissions = updatedPermissions.filter(permission => permission.hasPermission).length;

        if (selectedPermissions === 0) {
            setMainMenuSelectedStatus(''); // None are selected
        } else if (selectedPermissions === totalPermissions) {
            setMainMenuSelectedStatus('all'); // All are selected
        } else {
            setMainMenuSelectedStatus('partial'); // Some are selected
        }
    };

    // Helper function to update selectedMenuFeatureIds
    const updateSelectedMenuFeatureIds = (menuFeatureId: number, checked: boolean) : void=> {
        if (checked) {
            // Add to selectedMenuFeatureIds if not already present
            if (!selectedMenuFeatureIds.some(id => id === menuFeatureId || (typeof id === 'object' && id.menuFeatureId === menuFeatureId))) {
                selectedMenuFeatureIds.push(menuFeatureId);
            }
        } else {
            // Remove from selectedMenuFeatureIds if present
            const index = selectedMenuFeatureIds.findIndex(id => id === menuFeatureId || (typeof id === 'object' && id.menuFeatureId === menuFeatureId));
            if (index !== -1) {
                selectedMenuFeatureIds.splice(index, 1);
            }
        }
    };

    // Handle individual submenu checkbox change
    const handleCheckboxChange = (menuFeatureId: number, checked: boolean, menuType: string): void => {
        const updatedPermissions = permissions.map(permission =>
            permission.menuFeatureId === menuFeatureId
                ? { ...permission, hasPermission: checked }
                : permission
        );

        setPermissions(updatedPermissions);
        updateMainMenuStatus(updatedPermissions);

        // Update selectedMenuFeatureIds
        updateSelectedMenuFeatureIds(menuFeatureId, checked);

        // Notify parent about the selection change
        onItemSelect({ menuFeatureId }, checked, menuType);
    };

    // Handle main menu checkbox change (select/deselect all)
    const handleMainMenuCheckboxChange = (checked: boolean): void => {
        const updatedPermissions = permissions.map(permission => ({
            ...permission,
            hasPermission: checked,
        }));

        setPermissions(updatedPermissions);
        updateMainMenuStatus(updatedPermissions);

        // Notify parent for each submenu and update selectedMenuFeatureIds accordingly
        updatedPermissions.forEach(permission => {
            updateSelectedMenuFeatureIds(permission.menuFeatureId, checked);
            onItemSelect({ menuFeatureId: permission.menuFeatureId }, checked, 'sub');
        });
    };

    if (!menuData) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <Grid container spacing={0} alignItems="center" sx={{ marginLeft: '2px' }}>
                <Grid item xs={11} sx={{ display: 'flex' }}>
                    <FormGroup sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'bold' } }}>
                        <FormControlLabel
                            control={
                                <MUICheckbox
                                    checked={mainMenuSelectStatus === 'all'}
                                    indeterminate={mainMenuSelectStatus === 'partial'}
                                    onChange={() => handleMainMenuCheckboxChange(mainMenuSelectStatus !== 'all')}
                                />
                            }
                            label={menuData.menuName}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <Grid container spacing={0} alignItems="center" sx={{ paddingLeft: '25px' }}>
                <Grid item xs={11}>
                    {permissions.map(permission => (
                        <div key={permission.menuFeatureId} className="flex items-center mb-2">
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <MUICheckbox
                                            checked={permission.hasPermission}
                                            onChange={() =>
                                                handleCheckboxChange(permission.menuFeatureId, !permission.hasPermission, 'sub')
                                            }
                                        />
                                    }
                                    label={permission.menuFeature}
                                />
                            </FormGroup>
                        </div>
                    ))}
                </Grid>
            </Grid>

            <hr
                style={{
                    border: 'none',
                    borderBottom: '1px solid #e0e0e0',
                    width: '100%',
                    margin: '4px 0',
                }}
            />
        </div>
    );
}

export default PermissionTabContent;
